"use client";

import { useRef, useState } from "react";
import { Button, Dialog, Portal, VStack } from "@chakra-ui/react";
import usePageSearch from "../hooks/usePageSearch";
import useSearchCriteriaSelection from "../hooks/useSearchCriteriaSelection";
import NavbarSearchInput from "./NavbarSearchInput";
import SearchDialogHeader from "./SearchDialogHeader";
import SearchTargetFocusSection from "./SearchTargetFocusSection";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import { useUserChoices } from "@/state/globalStore";
import { Target } from "@/generated/fastapi";
import { useSystemDesign } from "@/shared/system-design";
import WithLongPress from "@/shared/ui/gestures/WithLongPress";
import { useTargetItems, useTargetsQuery } from "@/features/targets";

interface NavbarSearchProps {
  disabled?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  renderTrigger?: boolean;
}

// PARKED FEATURE:
// The chip-based search-settings workflow is intentionally kept in code but is
// currently unreachable from the navbar. Keep this logic intact for possible
// future reactivation instead of deleting it.
export default function NavbarSearch({
  disabled = false,
  open,
  onOpenChange,
  renderTrigger = true,
}: NavbarSearchProps) {
  const { targetsToRerank, setTargetsToRerank, resetTargetsToRerank } =
    useUserChoices();
  const { isMobileDevice } = useSystemDesign();
  const {
    hasActiveQuery,
    hasNonDefaultSearchAdjustments,
    relevantSearchConfig,
    resetPageSearch,
    breadcrumbs,
    showPerspectiveBreadcrumb,
    showTargetFocusSection,
    useTallDialog,
  } = usePageSearch();
  const targetsQuery = useTargetsQuery();
  const targetItems = useTargetItems(targetsQuery.data);

  const navSearchRef = useRef<HTMLTextAreaElement | null>(null);
  const [internalIsDialogOpen, setInternalIsDialogOpen] = useState(false);

  const { selectedCriteria, clearCriteria } = useSearchCriteriaSelection();

  const setTargetsToRerankWrapper = (targets: Target[]) => {
    if (selectedCriteria.length > 0) {
      clearCriteria();
    }
    setTargetsToRerank(targets);
  };

  const isHighlighted = hasActiveQuery || hasNonDefaultSearchAdjustments;

  function handleLongPressReset() {
    clearCriteria();
    resetPageSearch();
    resetTargetsToRerank();
  }

  const isDialogOpen = open ?? internalIsDialogOpen;

  function setIsDialogOpen(nextOpen: boolean) {
    if (open === undefined) {
      setInternalIsDialogOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  }

  return (
    <Dialog.Root
      size={isMobileDevice ? "md" : "lg"}
      open={isDialogOpen}
      onOpenChange={(details) => {
        setIsDialogOpen(details.open);
      }}
      // Search is the task here, so this is the deliberate exception to the
      // app overlay default of avoiding editable initial focus on open.
      initialFocusEl={() => navSearchRef.current}
    >
      {renderTrigger ? (
        <Dialog.Trigger asChild disabled={disabled}>
          <Button variant="ghost">
            <WithLongPress
              onLongPress={handleLongPressReset}
              enabled={isHighlighted}
              longPressHint={isHighlighted ? "Press long to reset" : undefined}
            >
              <GenericIcon
                icon="search_settings"
                size="sm"
                highlighted={isHighlighted}
                humanReadable="Search settings"
              />
            </WithLongPress>
          </Button>
        </Dialog.Trigger>
      ) : null}

      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            bg="moveon.surface.bg"
            color="moveon.surface.fg"
            borderColor="moveon.surface.border"
            py="4"
            px={{ base: "2", md: "4" }}
            scrollBehavior="inside"
            minH={useTallDialog ? "50vh" : undefined}
            maxH="90vh"
            overflowY="auto"
          >
            <VStack spaceY="2" align="stretch">
              <SearchDialogHeader
                breadcrumbs={breadcrumbs}
                showPerspectiveBreadcrumb={showPerspectiveBreadcrumb}
                hasActiveQuery={hasActiveQuery}
                hasNonDefaultSearchAdjustments={hasNonDefaultSearchAdjustments}
                onResetCriteria={() => clearCriteria()}
                onClose={() => setIsDialogOpen(false)}
                onLongPressReset={handleLongPressReset}
              />

              <NavbarSearchInput
                placeholder={`Search ${relevantSearchConfig.title} ...`}
                textareaRef={navSearchRef}
              />

              {showTargetFocusSection && (
                <SearchTargetFocusSection
                  targetsToRerank={targetsToRerank}
                  targetItems={targetItems}
                  targetSourceItems={targetsQuery.data ?? []}
                  isLoading={targetsQuery.isLoading || targetsQuery.isFetching}
                  onTargetsChange={setTargetsToRerankWrapper}
                />
              )}
            </VStack>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
